import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api, { obligacionesAPI, empleadosAPI, textoErrorApi } from '../api/api';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { User, Plus, Trash2, Eye, Users, Banknote, Search, RotateCcw } from 'lucide-react';
import RemesaNominasModal from '../components/RemesaNominasModal';
import { useAvisoTemporal, AvisoInline, DialogoConfirmar } from '../components/empleados/RegistroHorarioEmpleado';
import { hoyMadrid } from '../utils/fechas';

// "2026-10-06" (o ISO completo) -> "06/10/2026", sin pasar por zonas horarias
const fechaCorta = (valor) => {
  if (!valor) return '';
  const m = String(valor).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(valor);
};

const euros = (n) => `${Number(n || 0).toLocaleString('es-ES', { maximumFractionDigits: 2 })} €`;

// La cuenta "admin" no es una persona de la plantilla
const esCuentaAdmin = (e) => e.nombre?.toLowerCase() === 'admin';

const FILTROS = [
  { valor: 'activos', texto: 'Activos', activa: 'bg-green-600 text-white' },
  { valor: 'bajas', texto: 'Bajas', activa: 'bg-red-600 text-white' },
  { valor: 'todos', texto: 'Todos', activa: 'bg-blue-600 text-white' },
];

// Colores disponibles para empleados
const coloresDisponibles = [
  { value: 'amarillo', label: 'Amarillo', bg: 'bg-yellow-400', text: 'text-yellow-800' },
  { value: 'azul', label: 'Azul', bg: 'bg-blue-500', text: 'text-white' },
  { value: 'verde', label: 'Verde', bg: 'bg-green-500', text: 'text-white' },
  { value: 'naranja', label: 'Naranja', bg: 'bg-orange-500', text: 'text-white' },
  { value: 'morado', label: 'Morado', bg: 'bg-purple-500', text: 'text-white' },
  { value: 'rosa', label: 'Rosa', bg: 'bg-pink-400', text: 'text-white' },
  { value: 'rojo', label: 'Rojo', bg: 'bg-red-500', text: 'text-white' },
  { value: 'cyan', label: 'Cyan', bg: 'bg-cyan-500', text: 'text-white' },
];

const getColorClasses = (color) => {
  const c = coloresDisponibles.find((col) => col.value === color);
  return c ? `${c.bg} ${c.text}` : 'bg-gray-200 text-gray-800';
};

const Empleados = () => {
  // Plantilla completa (activos y bajas): el filtro se aplica en el cliente
  const [empleados, setEmpleados] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [filtro, setFiltro] = useState('activos');
  const [busqueda, setBusqueda] = useState('');
  // Obligaciones pendientes por empleado: { empleado_id: { faltan, pendientes: [textos] } }
  const [obligaciones, setObligaciones] = useState({});
  // Avisos inline (sustituyen a los alert del navegador)
  const { aviso, mostrarAviso, limpiarAviso } = useAvisoTemporal();

  // Dar de baja: { empleado, fecha } / Reactivar: empleado
  const [bajaPendiente, setBajaPendiente] = useState(null);
  const [reactivarPendiente, setReactivarPendiente] = useState(null);
  const [procesando, setProcesando] = useState(false);
  const [errorDialogo, setErrorDialogo] = useState('');

  // Remesa de nóminas (BBVA): se abre también desde la notificación con ?remesa=YYYY-MM
  // Enlaces antiguos con ?ficha=<id>: llevan a la ficha /empleados/<id>
  const [searchParams] = useSearchParams();
  const remesaParam = searchParams.get('remesa');
  const fichaParam = searchParams.get('ficha');
  const [showRemesa, setShowRemesa] = useState(false);
  useEffect(() => {
    if (remesaParam && /^\d{4}-\d{2}$/.test(remesaParam)) {
      setShowRemesa(true);
    }
  }, [remesaParam]);

  // Roles: solo para mostrar su nombre en la tabla
  const [roles, setRoles] = useState([]);

  // Resumen de obligaciones (lo que le falta a cada empleado). Si falla, sin badge.
  const fetchObligaciones = useCallback(async () => {
    try {
      const res = await obligacionesAPI.resumen();
      const mapa = {};
      (res.data?.empleados || []).forEach((r) => {
        mapa[r.empleado_id] = {
          faltan: r.faltan || 0,
          pendientes: (r.items || []).filter((it) => it.ok === false).map((it) => it.texto),
        };
      });
      setObligaciones(mapa);
    } catch (error) {
      setObligaciones({});
    }
  }, []);

  // silencioso: refresca sin pasar a "Cargando..." (tras dar de baja o reactivar)
  const fetchEmpleados = useCallback(async ({ silencioso = false } = {}) => {
    try {
      if (!silencioso) setLoading(true);
      const response = await empleadosAPI.getAll();
      setEmpleados(response.data || []);
    } catch (error) {
      console.error('Error fetching empleados:', error);
    } finally {
      setLoading(false);
    }
    fetchObligaciones();
  }, [fetchObligaciones]);

  // Plantilla y roles: una sola vez al montar
  useEffect(() => {
    fetchEmpleados();
    api.get('/roles')
      .then((res) => setRoles(res.data || []))
      .catch((error) => console.error('Error fetching roles:', error));
  }, [fetchEmpleados]);

  // ?ficha=<id> (enlaces antiguos): redirige a la ficha
  useEffect(() => {
    if (fichaParam) navigate(`/empleados/${encodeURIComponent(fichaParam)}`, { replace: true });
  }, [fichaParam, navigate]);

  const pedirBaja = (empleado) => {
    limpiarAviso();
    setErrorDialogo('');
    setBajaPendiente({ empleado, fecha: hoyMadrid() });
  };

  const confirmarBaja = async () => {
    if (!bajaPendiente) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(bajaPendiente.fecha || '')) {
      setErrorDialogo('Indica la fecha de baja');
      return;
    }
    setProcesando(true);
    setErrorDialogo('');
    try {
      const res = await api.post(`/empleados/${bajaPendiente.empleado.id}/dar-de-baja`, null, {
        params: { fecha_baja: bajaPendiente.fecha },
      });
      setBajaPendiente(null);
      fetchEmpleados({ silencioso: true });
      mostrarAviso('ok', res.data?.message || 'Baja registrada');
    } catch (error) {
      console.error('Error:', error);
      setErrorDialogo(textoErrorApi(error.response?.data?.detail, 'Error al dar de baja'));
    } finally {
      setProcesando(false);
    }
  };

  const pedirReactivar = (empleado) => {
    limpiarAviso();
    setErrorDialogo('');
    setReactivarPendiente(empleado);
  };

  const confirmarReactivar = async () => {
    if (!reactivarPendiente) return;
    setProcesando(true);
    setErrorDialogo('');
    try {
      await empleadosAPI.update(reactivarPendiente.id, { activo: true, baja: null });
      const nombre = reactivarPendiente.nombre;
      setReactivarPendiente(null);
      fetchEmpleados({ silencioso: true });
      mostrarAviso('ok', `${nombre} vuelve a estar activo`);
    } catch (error) {
      console.error('Error:', error);
      setErrorDialogo(textoErrorApi(error.response?.data?.detail, 'Error al reactivar'));
    } finally {
      setProcesando(false);
    }
  };

  // Plantilla sin la cuenta admin; el filtro y el buscador solo afectan a la tabla
  const plantilla = useMemo(() => empleados.filter((e) => !esCuentaAdmin(e)), [empleados]);
  const activos = useMemo(() => plantilla.filter((e) => e.activo !== false), [plantilla]);
  const bajasProgramadas = activos.filter((e) => e.baja).length;
  const nominaBruta = activos.reduce((sum, e) => sum + (e.salario_bruto || e.salario || 0), 0);
  const conPendientes = Object.values(obligaciones).filter((o) => o.faltan > 0).length;

  const visibles = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    return plantilla
      .filter((e) => (filtro === 'activos' ? e.activo !== false : filtro === 'bajas' ? e.activo === false : true))
      .filter((e) => !texto || [e.nombre, e.nombre_completo, e.dni, e.email]
        .some((v) => String(v || '').toLowerCase().includes(texto)));
  }, [plantilla, filtro, busqueda]);

  const nombreRol = (rol) => roles.find((r) => r.role_id === rol)?.display_name || rol || '-';

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-gray-600">Cargando empleados...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
          <Users className="w-8 h-8 text-gray-700" />
          Empleados
        </h1>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => setShowRemesa(true)}
            className="px-5 py-3 border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition flex items-center gap-2"
          >
            <Banknote className="w-5 h-5" />
            Remesa de nóminas
          </button>
          <button
            onClick={() => navigate('/empleados/nuevo')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Nuevo Empleado
          </button>
        </div>
      </div>

      <AvisoInline aviso={aviso} onCerrar={limpiarAviso} className="mb-4" testId="empleados-aviso" />

      {/* Buscador y filtro */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6 flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por nombre, DNI o email..."
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            data-testid="empleados-buscar"
          />
        </div>
        <div className="flex gap-2">
          {FILTROS.map((f) => (
            <button
              key={f.valor}
              onClick={() => setFiltro(f.valor)}
              className={`px-4 py-2 rounded-lg transition ${filtro === f.valor ? f.activa : 'bg-gray-200 text-gray-700'}`}
              data-testid={`empleados-filtro-${f.valor}`}
            >
              {f.texto}
            </button>
          ))}
        </div>
      </div>

      {/* Tarjetas */}
      <div className={`grid grid-cols-2 gap-4 mb-6 ${bajasProgramadas > 0 ? 'md:grid-cols-5' : 'md:grid-cols-4'}`}>
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <div className="text-2xl font-bold text-blue-800">{plantilla.length}</div>
          <div className="text-sm text-blue-700">Total</div>
        </div>
        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <div className="text-2xl font-bold text-green-800">{activos.length}</div>
          <div className="text-sm text-green-700">Activos</div>
        </div>
        <div className="bg-red-50 rounded-lg p-4 border border-red-200" data-testid="empleados-con-pendientes">
          <div className="text-2xl font-bold text-red-800">{conPendientes}</div>
          <div className="text-sm text-red-700">Con pendientes</div>
        </div>
        <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
          <div className="text-2xl font-bold text-purple-800">{euros(nominaBruta)}</div>
          <div className="text-sm text-purple-700">Nómina bruta/mes (bruto, activos)</div>
        </div>
        {bajasProgramadas > 0 && (
          <div className="bg-amber-50 rounded-lg p-4 border border-amber-200" data-testid="empleados-bajas-programadas">
            <div className="text-2xl font-bold text-amber-800">{bajasProgramadas}</div>
            <div className="text-sm text-amber-700">Bajas programadas</div>
          </div>
        )}
      </div>

      {/* Tabla de empleados */}
      <div className="bg-white rounded-lg shadow-md overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rol</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Contrato</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">Salario bruto</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Estado</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {visibles.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">No hay empleados con ese filtro</td>
              </tr>
            )}
            {visibles.map((empleado) => (
              <tr key={empleado.id} className="hover:bg-gray-50">
                <td className="px-4 py-4">
                  <div className="flex items-center">
                    {empleado.color ? (
                      <div className={`w-8 h-8 rounded-full ${getColorClasses(empleado.color)} flex items-center justify-center mr-2 text-xs font-bold shrink-0`}>
                        {empleado.nombre?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                    ) : (
                      <User className="w-5 h-5 text-gray-400 mr-2 shrink-0" />
                    )}
                    <div>
                      <Link to={`/empleados/${empleado.id}`} className="font-medium text-gray-900 hover:text-blue-700 hover:underline" data-testid={`empleado-nombre-${empleado.id}`}>
                        {empleado.nombre}
                      </Link>
                      {empleado.nombre_completo && empleado.nombre_completo !== empleado.nombre && (
                        <div className="text-xs text-gray-500">{empleado.nombre_completo}</div>
                      )}
                      {obligaciones[empleado.id]?.faltan > 0 && (
                        <span
                          className="inline-block mt-0.5 bg-red-100 text-red-700 text-[11px] font-bold rounded-full px-2"
                          title={`Pendiente: ${obligaciones[empleado.id].pendientes.join(', ')}`}
                          data-testid={`empleado-obligaciones-${empleado.id}`}
                        >
                          faltan {obligaciones[empleado.id].faltan}
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                    {nombreRol(empleado.rol)}
                  </span>
                </td>
                <td className="px-4 py-4 text-sm text-gray-700 hidden md:table-cell">
                  <div>Alta: {fechaCorta(empleado.fecha_alta) || '-'}</div>
                  {empleado.baja && (
                    <div className={empleado.activo ? 'text-amber-700' : 'text-red-600'}>Baja: {fechaCorta(empleado.baja)}</div>
                  )}
                </td>
                <td className="px-4 py-4 text-right text-sm font-medium text-gray-900 hidden lg:table-cell">
                  {euros(empleado.salario_bruto || empleado.salario || 0)}
                </td>
                <td className="px-4 py-4 text-center">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    empleado.activo
                      ? empleado.baja ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {empleado.activo ? (empleado.baja ? 'Baja programada' : 'Activo') : 'Baja'}
                  </span>
                </td>
                <td className="px-4 py-4 text-right">
                  <div className="flex justify-end gap-1">
                    <Link
                      to={`/empleados/${empleado.id}`}
                      className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                      title="Ficha del empleado"
                      data-testid={`empleado-ficha-${empleado.id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Link>
                    {empleado.activo ? (
                      <button
                        onClick={() => pedirBaja(empleado)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                        title="Dar de baja"
                        data-testid={`empleado-baja-${empleado.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => pedirReactivar(empleado)}
                        className="p-2 text-emerald-700 hover:bg-emerald-50 rounded-lg transition"
                        title="Reactivar"
                        data-testid={`empleado-reactivar-${empleado.id}`}
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dar de baja */}
      <DialogoConfirmar
        abierto={!!bajaPendiente}
        titulo={bajaPendiente ? `Dar de baja a ${bajaPendiente.empleado.nombre}` : ''}
        descripcion="Si la fecha es futura, queda como baja programada hasta ese día."
        textoConfirmar="Dar de baja"
        peligro
        ocupado={procesando}
        deshabilitado={!bajaPendiente?.fecha}
        error={errorDialogo}
        onConfirmar={confirmarBaja}
        onCancelar={() => { setBajaPendiente(null); setErrorDialogo(''); }}
        testId="dialogo-baja"
      >
        <label className="block text-sm font-medium text-gray-700">
          Fecha de baja
          <input
            type="date"
            value={bajaPendiente?.fecha || ''}
            onChange={(e) => setBajaPendiente((prev) => (prev ? { ...prev, fecha: e.target.value } : prev))}
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
            data-testid="dialogo-baja-fecha"
          />
        </label>
      </DialogoConfirmar>

      {/* Reactivar */}
      <DialogoConfirmar
        abierto={!!reactivarPendiente}
        titulo={reactivarPendiente ? `Reactivar a ${reactivarPendiente.nombre}` : ''}
        descripcion="Vuelve a estar activo y se borra la fecha de baja."
        textoConfirmar="Reactivar"
        ocupado={procesando}
        error={errorDialogo}
        onConfirmar={confirmarReactivar}
        onCancelar={() => { setReactivarPendiente(null); setErrorDialogo(''); }}
        testId="dialogo-reactivar"
      />

      <RemesaNominasModal
        open={showRemesa}
        onClose={() => setShowRemesa(false)}
        mesInicialStr={remesaParam}
      />
    </div>
  );
};

export default Empleados;
